/**
 * 版本更新判定（任务 15b，纯逻辑，可单测）。
 *
 * 背景约束（PROMPT「iOS Web Clip 交付」）：standalone 下没有地址栏、没有下拉
 * 刷新，用户**无法手动刷新**。所以新版本就绪后必须有两条出路：
 * 1. 提示条 +「立即刷新」按钮（两端都有，这是 standalone 下唯一的用户可控路径）；
 * 2. 安全时机自动应用（standalone 专属兜底，用户连提示条都不点时也能换新版）。
 *
 * 「安全时机」的口径（为什么不是「挂后台就刷」）：iOS 把后台的 Web Clip 整个
 * 冻结，挂后台期间的定时器根本不跑，「隐藏 N 秒后刷新」在 iOS 上是死代码。
 * 真正可用的沿是**回前台**：挂了足够久（≥ AUTO_APPLY_BACKGROUND_MS）再回来，
 * 本就是「像冷启动一样」的时刻——会话/滚动位置/草稿都由任务 13 从 IndexedDB
 * 恢复，刷新对用户不可见。限制三条，缺一不自动：
 * - 只在 standalone 自动（浏览器里用户自己能刷，不抢控制权）；
 * - outbox 有未发出/发送失败的消息时不自动（outbox 不持久化，刷新即丢，见 10a）；
 * - 回前台后用户一旦有交互（点/按键）就不再自动——人已经在用了，页面不能从
 *   手底下被抽走，改由提示条等用户点。
 * 所以自动只发生在「回前台 → 窗口期内新版本就绪 → 用户还没动手」这条缝里
 * （AUTO_APPLY_GRACE_MS）；错过就退化成提示条，不会无限期埋着一个随时刷新的雷。
 */

/** 回前台前至少挂了这么久，才算「用户离开过」=自动应用的安全沿 */
export const AUTO_APPLY_BACKGROUND_MS = 10 * 60_000;

/** 武装窗口：回前台后这段时间内新版本就绪才自动应用，更晚一律走提示条 */
export const AUTO_APPLY_GRACE_MS = 60_000;

/**
 * 主动查新版本的最小间隔（回前台沿触发）。
 * standalone 会话可能几天不导航，浏览器自己的更新检查跟着不触发——不主动查就
 * 永远停在旧版。代价是一次 `/sw.js` 的条件 GET（304），由本冷却门控住频率。
 */
export const UPDATE_CHECK_COOLDOWN_MS = 30 * 60_000;

/** 点了「立即刷新」后等 controllerchange 的上限，超时直接刷（见 swUpdateState） */
export const APPLY_FALLBACK_MS = 3_000;

/** 回前台沿：这次挂后台够久吗（够久=武装自动应用） */
export function shouldArmAutoApply(backgroundForMs: number): boolean {
  return backgroundForMs >= AUTO_APPLY_BACKGROUND_MS;
}

export interface AutoApplyEnv {
  /** 新版本已就绪（waiting worker 在位） */
  readonly ready: boolean;
  /** 已在应用中（避免重复触发） */
  readonly applying: boolean;
  readonly isStandalone: boolean;
  /** outbox 有 pending/failed 条目（刷新会丢，见文件头） */
  readonly hasPendingSends: boolean;
  /** 武装时刻（回前台沿写入）；null=未武装 */
  readonly armedAt: number | null;
  /** 武装后用户有过交互 */
  readonly interactedSinceArm: boolean;
  readonly now: number;
}

/** 现在可以自动应用新版本吗（口径见文件头） */
export function decideAutoApply(env: AutoApplyEnv): boolean {
  if (!env.ready || env.applying) return false;
  if (!env.isStandalone) return false;
  if (env.hasPendingSends) return false;
  if (env.armedAt === null || env.interactedSinceArm) return false;
  return env.now - env.armedAt <= AUTO_APPLY_GRACE_MS;
}

/**
 * controllerchange 要不要刷新页面。
 *
 * 必须判 hadController：15a 的 activate 里有 `clients.claim()`，**首次安装**也会
 * 给当前页触发一次 controllerchange——那页跑的就是最新代码，刷新纯属白屏闪烁
 * （而且首访就刷新，观感像崩了）。页面原本有 controller 才说明这是一次换版。
 * applying 兜底覆盖「本页刚发 SKIP_WAITING」的情形。
 * 多标签页：别的标签点了刷新，本页同样会收到这个事件并跟着刷——旧 JS 配新缓存
 * 会在懒加载 chunk 上直接 404，跟着刷才是对的。
 */
export function shouldReloadOnControllerChange(input: {
  hadController: boolean;
  applying: boolean;
}): boolean {
  return input.hadController || input.applying;
}

export interface UpdateCheckGate {
  /** 允许这次检查吗（允许则记下时刻，冷却期内后续调用返回 false） */
  allow(): boolean;
}

/** 更新检查冷却门（创建时即开始冷却：注册时浏览器刚取过一次 sw.js） */
export function createUpdateCheckGate(deps: {
  now(): number;
  cooldownMs?: number;
}): UpdateCheckGate {
  const cooldown = deps.cooldownMs ?? UPDATE_CHECK_COOLDOWN_MS;
  let last = deps.now();
  return {
    allow() {
      const t = deps.now();
      if (t - last < cooldown) return false;
      last = t;
      return true;
    },
  };
}
